import React from "react";
import styled from "styled-components";
import Form from "antd/lib/form";
import Radio from "antd/lib/radio";
import Checkbox from "antd/lib/checkbox";
import {
  FileTextOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

export const SurveyPage = styled.div<{ platform?: string | null }>`
  min-height: 100vh;
  box-sizing: border-box;
  padding: 24px 32px 48px;

  @media (max-width: 1024px) {
    padding: 20px 20px 36px;
  }

  @media (max-width: 768px) {
    padding: 12px 10px 28px;
  }
`;

export const SurveyContent = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  box-sizing: border-box;
`;

export const SurveyHeaderContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 130px;
  box-sizing: border-box;
  padding: 26px 32px;
  margin: 0 0 22px;
  overflow: hidden;
  border-radius: 14px;
  background: linear-gradient(115deg, #ffffff 0%, #f6faff 65%, #e8f2ff 100%);
  border-top: 7px solid #4284f3;
  box-shadow: 0 5px 18px rgba(26, 52, 80, 0.1);

  @media (max-width: 1024px) {
    min-height: 110px;
    padding: 20px 24px;
    gap: 16px;
    margin-bottom: 18px;
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 16px 14px;
    gap: 12px;
    margin-bottom: 14px;
    border-radius: 12px;
    border-top-width: 5px;
  }
`;

export const HeaderIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #e8f2ff;
  color: #2484f4;
  font-size: 26px;

  @media (max-width: 1024px) {
    width: 52px;
    height: 52px;
    flex-basis: 52px;
    font-size: 22px;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
    font-size: 18px;
  }
`;

export const HeaderCopy = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
`;

export const PageTitle = styled.div`
  color: #172f52;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
  word-break: break-word;

  @media (max-width: 1024px) {
    font-size: 22px;
  }

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const HeaderSubtitle = styled.div`
  margin-top: 4px;
  color: #607493;
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-top: 2px;
  }
`;

interface SurveyHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onBack?(): void;
}

export const SurveyHeader: React.FC<SurveyHeaderProps> = ({
  title,
  subtitle = "Help us understand how you are feeling",
  icon = <FileTextOutlined />
}) => (
  <SurveyHeaderContainer>
    <HeaderIcon>{icon}</HeaderIcon>
    <HeaderCopy>
      <PageTitle>{title}</PageTitle>
      {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
    </HeaderCopy>
  </SurveyHeaderContainer>
);

export const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px 28px;
  margin-bottom: 22px;
  box-sizing: border-box;
  border: 1px solid #d8e7fb;
  border-radius: 14px;
  background: linear-gradient(120deg, #f4f9ff, #eaf3ff);
  box-shadow: 0 4px 15px rgba(26, 52, 80, 0.07);

  @media (max-width: 1024px) {
    padding: 20px 22px;
    gap: 16px;
    margin-bottom: 18px;
  }

  @media (max-width: 768px) {
    gap: 10px;
    padding: 14px 12px;
    margin-bottom: 14px;
    border-radius: 12px;
  }
`;

export const InfoIcon = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: grid;
  place-items: center;
  border: 2px solid #70aaff;
  border-radius: 50%;
  color: #1877e8;
  font-size: 24px;
  background: #f5f9ff;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    font-size: 18px;
  }
`;

export const InfoContent = styled.div`
  color: #1b3154;
  font-size: 14px;
  line-height: 1.55;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 1.45;
  }
`;

export const InfoTitle = styled.div`
  margin-bottom: 4px;
  color: #1672e6;
  font-size: 17px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export const InfoText = styled.div`
  font-weight: 500;
`;

export const InfoEmergency = styled.div`
  margin-top: 4px;
  font-weight: 800;
  color: #1d3356;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const SurveyNoticeCard: React.FC = () => (
  <InfoCard>
    <InfoIcon>
      <InfoCircleOutlined />
    </InfoIcon>
    <InfoContent>
      <InfoTitle>Important Information</InfoTitle>
      <InfoText>
        Please do not rely upon this survey for immediate medical attention.
        This survey may not be reviewed by your care team immediately. Please
        call your doctor for any medical problems.
      </InfoText>
      <InfoEmergency>
        IF YOU THINK THIS IS A MEDICAL EMERGENCY, OR YOUR SYMPTOMS ARE WORRISOME
        PLEASE CALL 911 OR GO TO THE NEAREST EMERGENCY ROOM.
      </InfoEmergency>
    </InfoContent>
  </InfoCard>
);

export const QuestionCard = styled.div`
  padding: 26px 30px;
  margin-bottom: 22px;
  box-sizing: border-box;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 5px 18px rgba(26, 52, 80, 0.09);

  @media (max-width: 1024px) {
    padding: 20px 22px;
    margin-bottom: 18px;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    padding: 16px 12px;
    margin-bottom: 14px;
    border-radius: 10px;
  }

  .ant-input,
  .ant-input-number,
  .ant-select,
  .ant-picker {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 14px;
  }
`;

export const QuestionIcon = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #eaf3ff;
  color: #2383ee;
  font-size: 22px;

  @media (max-width: 1024px) {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
    font-size: 20px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    font-size: 17px;
    margin-top: 2px;
  }
`;

export const FormItemStyle = styled(Form.Item)`
  margin-bottom: 0;

  .ant-form-item-control-input {
    min-height: auto;
  }
`;

export const AsteriskStyle = styled.span`
  margin-left: 3px;
  font-weight: 800;
  color: #e53935;
`;

export const CardTitle = styled.div`
  color: #172f52;
  font-size: 16px;
  line-height: 1.45;
  font-family: sans-serif;
  font-weight: 700;
  flex: 1;
  min-width: 0;
  word-break: break-word;

  @media (max-width: 1024px) {
    font-size: 15px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const SectionTag = styled.div`
  display: inline-block;
  padding: 4px 12px;
  margin-bottom: 12px;
  border-radius: 6px;
  background: #e5effe;
  color: #1672e6;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 3px 8px;
    margin-bottom: 8px;
  }
`;

export const SymptomAlertCard: React.FC<{
  title?: string;
  message: string;
}> = ({ title = "SYMPTOM ALERT", message }) => (
  <QuestionCard
    style={{
      borderLeft: "6px solid #e65100",
      marginTop: "20px",
      background: "#fff8f5"
    }}
  >
    <QuestionHeader>
      <QuestionIcon style={{ background: "#ffe0b2", color: "#e65100" }}>
        <ExclamationCircleOutlined />
      </QuestionIcon>
      <div>
        <div
          style={{
            color: "#e65100",
            fontSize: "14px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}
        >
          {title}
        </div>
        <div
          style={{
            color: "#2d3748",
            fontSize: "16px",
            fontWeight: 600,
            marginTop: "4px"
          }}
        >
          {message}
        </div>
      </div>
    </QuestionHeader>
  </QuestionCard>
);

interface SurveyQuestionCardProps {
  title?: React.ReactNode;
  required?: boolean;
  icon?: React.ReactNode;
  sectionTag?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const SurveyQuestionCard: React.FC<SurveyQuestionCardProps> = ({
  title,
  required = false,
  icon = <ThunderboltOutlined />,
  sectionTag,
  children,
  style
}) => (
  <QuestionCard style={style}>
    {sectionTag && <SectionTag>{sectionTag}</SectionTag>}
    {title && (
      <QuestionHeader>
        <QuestionIcon>{icon}</QuestionIcon>
        <CardTitle>
          {title}
          {required && <AsteriskStyle> *</AsteriskStyle>}
        </CardTitle>
      </QuestionHeader>
    )}
    {children}
  </QuestionCard>
);

export const StyledAnswerOption = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 8px 16px;
  box-sizing: border-box;
  border: 1.5px solid #d5dfec;
  border-radius: 12px;
  background: #fbfdff;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #4284f3;
    background: #f4f8ff;
  }

  .ant-radio-wrapper,
  .ant-checkbox-wrapper {
    width: 100%;
    height: 100%;
    margin-right: 0 !important;
    padding: 0 !important;
    color: #172f52;
    font-size: 16px;
    font-weight: 600;
    display: flex !important;
    align-items: center !important;
    line-height: 1.35;
    white-space: normal;
    word-break: break-word;
    cursor: pointer;
  }

  .ant-radio,
  .ant-checkbox {
    flex: 0 0 20px !important;
    width: 20px !important;
    height: 20px !important;
    margin-right: 12px !important;
    top: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .ant-radio-inner,
  .ant-checkbox-inner {
    width: 20px !important;
    height: 20px !important;
    border-color: #94a3b8 !important;
    border-width: 2px !important;
    box-sizing: border-box !important;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: #2484f4 !important;
    background-color: #ffffff !important;
  }

  .ant-radio-inner::after {
    width: 10px !important;
    height: 10px !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) scale(0) !important;
    margin: 0 !important;
    background-color: #2484f4 !important;
    border-radius: 50% !important;
    transition: all 0.15s ease-in-out;
  }

  .ant-radio-checked .ant-radio-inner::after {
    transform: translate(-50%, -50%) scale(1) !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #2484f4 !important;
    border-color: #2484f4 !important;
  }

  .ant-checkbox-inner::after {
    width: 5.7px !important;
    height: 9.1px !important;
    top: 45% !important;
    left: 25% !important;
    border-color: #ffffff !important;
  }

  .ant-radio-wrapper > span:last-child,
  .ant-checkbox-wrapper > span:last-child {
    flex: 1 1 auto;
    padding-left: 0 !important;
    padding-right: 0 !important;
    min-width: 0;
  }

  @media (max-width: 1024px) {
    min-height: 50px;
    padding: 8px 14px;
    border-radius: 10px;

    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
      font-size: 15px;
    }
  }

  @media (max-width: 600px) {
    min-height: 46px;
    padding: 6px 10px;
    border-radius: 8px;

    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
      font-size: 14px;
    }

    .ant-radio,
    .ant-checkbox {
      margin-right: 8px !important;
    }
  }
`;

export const AnswerGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, minmax(0, 1fr));
  gap: 12px;

  ${StyledAnswerOption} {
    padding: 0 10px;
  }

  @media (max-width: 768px) {
    grid-template-columns: ${props =>
      props.columns && props.columns > 3 ? "repeat(5, minmax(0, 1fr))" : "1fr"};
    gap: 8px;

    ${StyledAnswerOption} {
      padding: 0 4px;
      justify-content: center;

      .ant-radio-wrapper,
      .ant-checkbox-wrapper {
        justify-content: center;
      }
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: ${props =>
      props.columns && props.columns > 3 ? "repeat(5, minmax(0, 1fr))" : "1fr"};
    gap: 4px;

    ${StyledAnswerOption} {
      padding: 0 2px;
      min-height: 44px;

      .ant-radio,
      .ant-checkbox {
        margin-right: 4px !important;
      }

      .ant-radio-wrapper,
      .ant-checkbox-wrapper {
        font-size: 13px;
      }
    }
  }
`;

export const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export interface AnswerOptionProps {
  children?: React.ReactNode;
  value?: string;
  isCheckbox?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
}

export const AnswerOption: React.FC<AnswerOptionProps> = ({
  children,
  value,
  isCheckbox,
  style,
  className,
  onClick
}) => {
  let content = children;
  if (value !== undefined && value !== null) {
    if (isCheckbox) {
      content = <Checkbox value={value}>{children}</Checkbox>;
    } else {
      content = <Radio value={value}>{children}</Radio>;
    }
  }

  return (
    <StyledAnswerOption style={style} className={className} onClick={onClick}>
      {content}
    </StyledAnswerOption>
  );
};

export const SurveyFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-top: 8px;
  margin-top: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    margin-top: 14px;
  }
`;

export const ProgressSection = styled.div`
  flex: 1;
  max-width: 520px;
  width: 100%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const ProgressLabel = styled.div`
  margin-bottom: 8px;
  color: #647a9a;
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #dfe8f4;
`;

export const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #4284f3;
  transition: width 0.25s ease;
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;

    button,
    .ant-btn {
      flex: 1 1 auto;
      min-height: 44px;
      font-size: 15px;
    }
  }
`;

interface SurveyProgressBarProps {
  currentPage?: number;
  totalSteps?: number;
  totalPages?: number;
  percent?: number;
  currentStep?: number;
}

export const SurveyProgressBar: React.FC<SurveyProgressBarProps> = ({
  currentPage,
  totalSteps,
  totalPages,
  percent,
  currentStep
}) => {
  const step = currentStep ?? currentPage ?? 1;
  const total = totalSteps ?? totalPages ?? 1;
  const percentage =
    percent ?? Math.min(100, Math.max(0, Math.round((step / total) * 100)));
  return (
    <ProgressSection style={{ marginBottom: "0px" }}>
      <ProgressLabel>
        Step {step} of {total}
      </ProgressLabel>
      <ProgressTrack>
        <ProgressFill style={{ width: `${percentage}%` }} />
      </ProgressTrack>
    </ProgressSection>
  );
};
